import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit
} from '@angular/core';
import { ColorModel } from '@Common/Forms/models/color.model';
import { MAX_FAVORITE_COLORS } from '@Common/Forms/components/AdvancedColorPickerPopover/service/favorite-colors.service';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { AdvancedColorPickerFieldModel } from '@Common/PageBuilder/models/advanced-color-picker-field.model';

export interface FavoriteColorItem {
    color: string;
    duplicated: boolean;
    colorModel: ColorModel;
}

@Component({
    selector: 'agpr-favorite-colors-preferences',
    templateUrl: './favorite-colors-preferences.component.html',
    styleUrls: ['./favorite-colors-preferences.component.scss'],
    standalone: false
})

export class FavoriteColorsPreferencesComponent implements OnInit {

    @Input() set favoriteColors(colors: string[]) {
        this._favoriteColors = colors || [];
        this.buildColorItems();
    }
    get favoriteColors(): string[] {
        return this._favoriteColors;
    }

    @Output() colorsChanged = new EventEmitter<string[]>();

    colorItems: FavoriteColorItem[] = [];
    canAddMore: boolean = false;
    updateColorModel =  AdvancedColorPickerFieldModel.create({
        tooltip: 'ColorPicker.openColorPicker',
        showDeleteButton: true,
        showFavoriteColors: false
    });

    readonly id = 'FavoriteColorsPreferences'
    readonly MAX_COLOR_COUNT = MAX_FAVORITE_COLORS;
    readonly DEFAULT_COLOR_VALUE = '#FFFFFF';
    readonly buttonTypesEnum = ButtonTypesEnum;

    private _favoriteColors: string[] = [];

    constructor() {}

    ngOnInit(): void {
        this.buildColorItems();
    }


    private buildColorItems(): void {
        const colorMap = this._favoriteColors.reduce((map, color: string) => {
            map.set(color, (map.get(color) || 0) + 1);
            return map;
        }, new Map<string, number>());

        this.colorItems = this._favoriteColors.map(color => {
            const count = (colorMap.get(color) || 0);
            return {
                color: color.toUpperCase(),
                duplicated: count > 1,
                colorModel: ColorModel.createFromHex(color)
            };
        });

        this.canAddMore = this.colorItems.length < this.MAX_COLOR_COUNT;
    }

    startAddingColor(): void {
        if (this.canAddMore) {
            this._favoriteColors = [this.DEFAULT_COLOR_VALUE, ...this._favoriteColors];
            this.buildColorItems();
            this.emitChanges();
        }
    }

    updateColor(index: number, newColor: string): void {
        const normalizedColor = newColor.toUpperCase();
        if (this._favoriteColors[index] !== normalizedColor) {
            this._favoriteColors[index] = normalizedColor;
            this.buildColorItems();
            this.emitChanges();
        }
    }


    removeColor(index: number): void {
        if (index < 0 || index >= this._favoriteColors.length) return;

        // Remove the color at the exact index
        this._favoriteColors.splice(index, 1);

        this.buildColorItems();
        this.emitChanges();
    }

    private emitChanges(): void {
        this.colorsChanged.emit([...this._favoriteColors]);
    }
}
